import { Injectable, inject } from '@angular/core';
import { map, take } from 'rxjs/operators';
import { ConstructAPI } from 'src/app/API/constructAPI';
import { RestApiService } from 'src/app/API/restapi';
import { IAPITransaction, Transaction } from 'src/model/transaction.model';
import { TransactionSubType } from 'src/model/transactionSubType.model';
import { TransactionType } from 'src/model/transactionType.model';

export interface SubType {
  assignable: boolean,
  enabled: boolean,
  id: number,
  name: string,
  type: number
}

export interface TransactionBalances {
  balance: number,
  companyId: number,
  type: number
}

interface transactionOptions {
  scheme?: string,
  startDate?: string,
  endDate?: string,
  types?: number[],
  accountId?: number,
  minAmount?: number,
  maxAmount?: number,
  handledBy?: string,
  subtype?: number,
  icNumber?: string,
  customerId?: number,
  receiptId?: number,
  lastId?: number,
  lastDate?: string,
  sortOrder?: string,
  limit?: number
}

  interface transBalanceOptions {
    companyGroup?: number,
    companyId?: number,
    endDate?: Date,
    types?: number[],
  }

@Injectable({
  providedIn: 'root'
})
export class TransactionAdapter {

  constructor() { }

  adaptType(item: any): TransactionType {
    return new TransactionType(item.id, item.name);
  }

  adaptSubtype(item: any): TransactionSubType {
    return new TransactionSubType(
      item.id,
      item.name,
      item.type,
      item.assignable,
      item.enabled
    )
  }

  // toTypeDict(items: any[]): {[key: number]: string} {
  //   return items.reduce((acc, item) => {
  //     acc[item.id] = item.name;
  //     return acc;
  //   }, {} as { [key: number]: string });
  // }

  // toSubtypeDict(items: any): {[key: number]: string} {
  //   return items.reduce((acc, item) => {
  //     acc[item.id] = item.name;
  //     return acc;
  //   }, {} as { [key: number]: string });
  // }

  adapt(item: any): Transaction {
    return new Transaction(
      item.id,
      item.remark,
      item.amount,
      item.closingBalance,
      item.accountId,
      new Date(item.date),
      item.type,
      item.handleBy,
      item.receiptID,
      item.customerID,
      item.customerName,
      item.customerIC,
      item.customerOccupation,
      item.subtype,
      item.groupId,
      item.setId,
      item.loanId
    )
  }

  toApiModel(trans: Transaction): IAPITransaction {
    return {
      id: trans.ID,
      remark: trans.Remark,
      amount: trans.Amount,
      closingBalance: trans.ClosingBalance,
      accountId: trans.AccountId,
      date: trans.Date.toISOString(),
      type: trans.Type,
      handleBy: trans.HandleBy,
      receiptID: trans.ReceiptID,
      customerID: trans.CustomerID,
      customerName: trans.CustomerName,
      customerIC: trans.CustomerIC,
      customerOccupation: trans.CustomerOccupation,
      subtype: trans.Subtype,
      groupId: trans.GroupId,
      setId: trans.SetId,
      loanId: trans.LoanId
    };
  }
}

@Injectable({
  providedIn: 'root'
})
export class TransactionService {
  private api = inject(RestApiService);
  private constructAPI = inject(ConstructAPI);
  private adapter = inject(TransactionAdapter);

  // getAllTransactionTypesDict() {
  //   return new Promise<{ [key: number]: string }> ((resolve, reject) => {
  //     this.api.get<TransactionType[]>(this.constructAPI.getAllTransactionTypes()).pipe(
  //       map(data => this.adapter.toTypeDict(data)),
  //     ).subscribe({
  //       next: res => resolve(res),
  //       error: err => {
  //         this.api.handleError(err);
  //         console.error(err);
  //         reject(err);
  //       }
  //     })
  //   });
  // }

  getAllTransactionTypes(): Promise<TransactionType[]> {
    return new Promise<TransactionType[]>((resolve, reject) => {
      this.api.get<TransactionType[]>(this.constructAPI.getAllTransactionTypes()).pipe(
        map(data => data.map(d => this.adapter.adaptType(d))),
      ).subscribe({
        next: res => resolve(res),
        error: err => {
          this.api.handleError(err);
          console.error(err);
          reject(err);
        }
      })
    });
  }

  // getAllTransactionSubTypesDict() {
  //   return new Promise<{ [key: number]: string }> ((resolve, reject) => {
  //     this.api.get<TransactionSubType[]>(this.constructAPI.getAllTransactionSubTypes()).pipe(
  //       map(data => this.adapter.toSubtypeDict(data)),
  //     ).subscribe({
  //       next: res => resolve(res),
  //       error: err => {
  //         this.api.handleError(err);
  //         console.error(err);
  //         reject(err);
  //       }
  //     })
  //   });
  // }

  getAllTransactionSubTypes(): Promise<TransactionSubType[]> {
    return new Promise<TransactionSubType[]>((resolve, reject) => {
      this.api.get<TransactionSubType[]>(this.constructAPI.getAllTransactionSubTypes()).pipe(
        map(data => data.map(d => this.adapter.adaptSubtype(d))),
      ).subscribe({
        next: res => resolve(res),
        error: err => {
          this.api.handleError(err);
          console.error(err);
          reject(err);
        }
      })
    });
  }

  getTransactionSubTypes(type: number): Promise<TransactionSubType[]> {
    return new Promise<TransactionSubType[]>((resolve, reject) => {
      this.api.get<TransactionSubType[]>(this.constructAPI.getTransactionSubTypes(type)).pipe(
        map(data => data.map(d => this.adapter.adaptSubtype(d))),
      ).subscribe({
        next: res => resolve(res),
        error: err => {
          this.api.handleError(err);
          console.error(err);
          reject(err);
        }
      })
    });
  }

  patchSetTransactionSubtype(transId: number, subtype: number): Promise<Transaction> {
    const data = { subtype: subtype };
    return new Promise<Transaction>((resolve, reject) => {
      this.api.patch(this.constructAPI.getEditTransactionSubtype(transId), data).subscribe({
        next: response => resolve(this.adapter.adapt(response)),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to update receipt set transaction subtypes: ', err);
          reject(err);
        }
      })
    })
  }

  getProfileLoansTransactions(profileID: number, o?: transactionOptions): Promise<Transaction[]> {
    return new Promise<Transaction[]>((resolve, reject) => {
      this.api.get<any>(this.constructAPI.getProfileAllLoansTransactions(profileID, o?.startDate, o?.endDate, o?.scheme, o?.types, o?.accountId, o?.handledBy, o?.minAmount, o?.maxAmount)).pipe(
        map(data => data.flatMap(d => d.transactions).map(t => this.adapter.adapt(t)))
      ).subscribe({
        next: res => resolve(res),
        error: err => {
          this.api.handleError(err);
          console.error(err);
          reject(err);
        }
      })
    });
  }

  //#region Reserve Transaction
  getProfileReserveReceipts(profileID: number): Promise<Transaction[]> {
    return new Promise<Transaction[]>((resolve, reject) => {
      this.api.get<any>(this.constructAPI.getProfileReserveReceipts(profileID)).pipe(
        map(data => data.flatMap(d => d.transactions).map(t => this.adapter.adapt(t)))
      ).subscribe({
        next: res => resolve(res),
        error: err => {
          this.api.handleError(err);
          console.error(err);
          reject(err);
        }
      })
    });
  }

  deleteReserveTransaction(id: number, reason: string) {
    const data = { reason: reason };
    return new Promise((resolve, reject) => {
      this.api.delete(this.constructAPI.getDeleteReserveTransaction(id), data).subscribe({
        next: response => resolve(response),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to delete reserve transaction: ', err);
          reject(err);
        }
      })
    })
  }
  //#endregion

  getTransactionBalances(o?: transBalanceOptions) {
    return new Promise<TransactionBalances[]>((resolve, reject) => {
      this.api.get<any>(this.constructAPI.getTransactionBalances(o?.companyGroup === 0 ? undefined : o?.companyGroup, o?.companyId,  o?.endDate?.toISOString(), o?.types)).pipe(
        map(data => data.map(d => ({ balance: d.balance, companyId: d.companyId, type: d.type }))),
        take(1)
      ).subscribe({
        next: res => resolve(res),
        error: err => {
          this.api.handleError(err);
          console.error(err);
          reject(err);
        }
      })
    });
  }
}
